// SF6 — Return Rate by SKU.
//
// This is where the return rate is defined for the whole product; other
// surfaces quote it rather than computing their own.
//
// Three outcomes that must not look alike. A rate. A genuine zero, meaning the
// product sold enough and none came back. And a blank, meaning too little
// volume to say anything — which is not zero and must never render as it.
//
// The table carries rate and volume only. Reasons appear on drill-down, because
// a reason column across a whole catalogue is a grid of single counts.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderAnalytics.module.css';
import { getReturns } from '../services/orderAnalyticsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

// A rate we could not measure renders blank, and says why on hover. A measured
// zero renders as 0%, which is a finding rather than an absence.
const Rate = ({ row }) => {
  if (row.return_rate === null || row.return_rate === undefined) {
    return (
      <span className={local.blank} title={`Only ${row.units_sold} units sold — too few for a rate to mean anything`}>
        —
      </span>
    );
  }
  const heavy = row.return_rate >= 0.1;
  return (
    <span className={heavy ? local.rateHigh : undefined}>
      {(row.return_rate * 100).toFixed(1)}%
    </span>
  );
};

const ReturnRates = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getReturns(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);
  useEffect(() => { setExpanded(null); setDetail({}); }, [data]);

  // Fetched once per product and kept, so collapsing and reopening a row does
  // not go back to the network.
  const toggle = useCallback(async (asin) => {
    if (expanded === asin) { setExpanded(null); return; }
    setExpanded(asin);
    if (detail[asin] || !token) return;
    try {
      const result = await getReturns(token, { start, end, asin });
      setDetail((current) => ({ ...current, [asin]: result }));
    } catch {
      setDetail((current) => ({ ...current, [asin]: { reasons: [] } }));
    }
  }, [expanded, detail, token, start, end]);

  const rows = data?.rows || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Return rate by product</h2>
        {data && (
          <span className={local.muted}>
            {count(data.returns)} returns · {count(data.units_returned)} units
          </span>
        )}
      </div>
      <p className={local.muted}>
        Buyer returns after delivery, counted against the period the order was
        placed rather than when the return came in. Open a row for its reasons.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load return rates'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {!error && data && (
        <>
          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              Why things come back
              <span className={local.groupCount}>account-wide</span>
            </h3>
          </div>
          <div className={local.reasonRow}>
            {(data.reasons || []).slice(0, 8).map((reason) => (
              <span key={reason.reason} className={local.reasonChip}>
                {reason.label}
                <strong>{count(reason.units)}</strong>
              </span>
            ))}
          </div>

          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              By product
              <span className={local.groupCount}>
                {count(data.measurable_products)} of {count(data.products)} have enough volume
              </span>
            </h3>
          </div>
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th />
                  <th>ASIN</th>
                  <th className={local.numeric}>Units sold</th>
                  <th className={local.numeric}>Units returned</th>
                  <th className={local.numeric}>Returns</th>
                  <th className={local.numeric}>Return rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td className={local.emptyCell} colSpan={6}>No orders in this period.</td></tr>
                ) : rows.map((row) => (
                  <React.Fragment key={row.asin}>
                    <tr className={local.clickableRow} onClick={() => toggle(row.asin)}>
                      <td className={local.chevron}>
                        {expanded === row.asin ? <FiChevronDown /> : <FiChevronRight />}
                      </td>
                      <td className={local.asinCell}>{row.asin}</td>
                      <td className={local.numeric}>{count(row.units_sold)}</td>
                      <td className={local.numeric}>{count(row.units_returned)}</td>
                      <td className={local.numeric}>{count(row.returns)}</td>
                      <td className={local.numeric}><Rate row={row} /></td>
                    </tr>
                    {expanded === row.asin && (
                      <tr>
                        <td colSpan={6} className={local.detailCell}>
                          {!detail[row.asin] ? 'Loading reasons…'
                            : detail[row.asin].reasons?.length ? (
                              <div className={local.reasonRow}>
                                {detail[row.asin].reasons.map((reason) => (
                                  <span key={reason.reason} className={local.reasonChip}>
                                    {reason.label}<strong>{count(reason.units)}</strong>
                                  </span>
                                ))}
                              </div>
                            ) : 'Nothing came back from this product in this period.'}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {data.definition && (
            <div className={local.provisional}>
              This page owns the return rate definition; other surfaces quote it.
              It covers {data.definition.scope.toLowerCase()} It divides by{' '}
              {data.definition.denominator}, and a product needs at least{' '}
              {data.definition.min_units} units sold before a rate is shown at
              all — below that the cell is blank, not zero. {data.definition.note}
            </div>
          )}
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ReturnRates;
